import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, PauseCircle, PlayCircle, RefreshCw, Trash2, XCircle } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import StepUpDialog from './StepUpDialog';
import PriceChangeTable, { overToleranceRows, priceRows, resultRows } from './PriceChangeTable';
import { makeCommandId } from '../components/InvoiceApprovalReview';
import { invoiceCommand, removeUpload } from './api';
import RejectDetails, { problemsPayload } from './RejectDetails';
import LotLabels from './LotLabels';
import { RevisionBanner, RevisionChoice } from './RevisionNotice';

const big = 'inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl font-medium disabled:opacity-50 motion-organic';

/** Approve (with a preview of price changes), Reject, Hold / Release hold and the demo-only Remove upload. */
// costingLink: the tenant has the price-impact screen (its menu shows nav-cut-cost-price-impact). Phase 1 has no
// costing, so the link would open a screen the server refuses.
// site / route: where the stock goes and who must approve (GET /api/review/invoices/{id}: `site`, `approval_route`).
// Approve is offered only once a site is known and to someone other than the uploader (the server refuses both too).
// lotLabels: `lot_labels` of that same read ({site_id, lot_ids}), the lots this invoice created: an approved invoice
// offers "Print lot labels" for them (H5); canPrintLabels is false for a role the kitchen's label route does not serve.
// priceTolerancePct: org policy for H2 / IN-05 over-tolerance ack before approve.
// revision: that read's `revision` (RevisionNotice.js): a new version of an earlier invoice, approved by choosing
// Replace or Keep both. A same-number duplicate without one is offered the same choice.
export default function ApprovePanel({ invoice, openTasks, extractionTask = false, items, reloadItems, onChanged, canRemove, onRemoved, costingLink = false, canDecide = true, site = null, route = null, lotLabels = null, canPrintLabels = true, priceTolerancePct = 5, priceToleranceLines = null, revision = null }) {
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [revAction, setRevAction] = useState(null);       // 'replace' | 'keep_both'
  const linked = !!(invoice.revision_of || invoice.duplicate_of);
  const [code, setCode] = useState(null);
  const [problems, setProblems] = useState([{ field: '', line_index: 0, actual: '' }]);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [before, setBefore] = useState(null);
  const [priceAck, setPriceAck] = useState(false);
  const cmd = useRef({});
  const state = invoice.state;
  const onHold = !!invoice.hold;
  const rows = priceRows(invoice, items);
  // T7: prefer the server's over-tolerance list (same function approve uses); fall back to client recompute.
  const overs = Array.isArray(priceToleranceLines)
    ? priceToleranceLines
    : overToleranceRows(rows.filter((r) => !r.history), priceTolerancePct);
  const waiting = canDecide && state === 'awaiting_approval' && !onHold;
  const needsSite = !!site?.needs_choice;
  const selfBlocked = !!route?.self_approval_blocked;
  const mayApprove = waiting && !needsSite && !selfBlocked;

  const commandFor = (action) => {
    if (!cmd.current[action]) cmd.current[action] = makeCommandId(`inv-${action}`);
    return cmd.current[action];
  };
  const close = () => {
    setDialog(null); setReason(''); setError(null); setCode(null); setPriceAck(false); setRevAction(null);
    setProblems([{ field: '', line_index: 0, actual: '' }]);
  };

  // Approving writes prices and re-costs dishes, then the page reloads: that can take a while. The dialog closes
  // as soon as it is confirmed and the panel shows "Posting prices…" until it is done, so the person is not
  // held on a popup. A failure shows on the panel with Approve available again.
  const send = async (action, extra = {}) => {
    const inBackground = action === 'approve';
    setError(null);
    if (inBackground) { setDialog(null); setReason(''); setPosting(true); } else setBusy(true);
    try {
      await invoiceCommand(invoice.id, action, { command_id: commandFor(action), ...extra });
      cmd.current[action] = null;
      if (action === 'approve') {
        setBefore(Object.fromEntries(rows.map((r) => [r.itemId, { price: r.oldPrice, unit: r.oldUnit }])));
        await reloadItems(rows.map((r) => r.itemId));
      }
      if (!inBackground) close();
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      if (inBackground) setPosting(false); else setBusy(false);
    }
  };

  // An approved invoice also undoes the prices it set: that needs a second check (StepUpDialog), not one click.
  const approved = state === 'approved';
  const remove = async () => {
    if (approved) { setError(null); setDialog('remove-step-up'); return; }
    setBusy(true);
    try { await removeUpload(invoice.id); close(); onRemoved(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-4" data-testid="approve-panel">
      {state === 'needs_review' && (
        <p className="text-ink" data-testid="approve-blocked">
          <strong>{openTasks}</strong> line{openTasks === 1 ? '' : 's'} still need{openTasks === 1 ? 's' : ''} a decision before this invoice can be approved.
        </p>
      )}
      {extractionTask && (
        <p className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800" data-testid="extraction-task-notice">
          Some figures on this invoice could not be read with confidence. Fix them under{' '}
          <strong>Correct what was read</strong> above: once they add up, the lines are matched and it moves on.
        </p>
      )}
      {posting && (
        <p className="flex items-center gap-2 rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-primary-700"
          role="status" data-testid="approve-pending">
          <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden="true" />
          Posting prices… you can keep working; this updates when it is done.
        </p>
      )}
      {mayApprove && !posting && (
        <p className="text-ink" data-testid="approve-ready">Every line is sorted. Check the price changes, then approve.</p>
      )}
      {linked && (state === 'awaiting_approval' || state === 'needs_review') && <RevisionBanner revision={revision} />}
      {state === 'superseded' && (
        <p className="text-ink-muted" data-testid="superseded-banner">
          Replaced by a corrected version. Its prices and stock now come from that invoice; this one is kept for the record.
        </p>
      )}
      {waiting && needsSite && (
        <p className="text-ink" data-testid="approve-needs-site">
          Choose the site this invoice&apos;s stock goes to (above), then approve.
        </p>
      )}
      {waiting && !needsSite && selfBlocked && (
        <p className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800" data-testid="approve-someone-else">
          You uploaded this invoice, so someone else must approve it: an owner, an admin or a manager
          of {site?.name || 'its site'}. You can still hold or reject it.
        </p>
      )}
      {!canDecide && (state === 'awaiting_approval' || state === 'needs_review' || onHold) && (
        <p className="text-ink" data-testid="approve-manager-only">
          A manager checks this invoice and approves, holds or rejects it. Nothing changes in stock or prices until then.
        </p>
      )}
      {onHold && (
        <p className="rounded-xl bg-warn/10 border border-warn/40 px-4 py-3 text-ink" data-testid="hold-banner">
          On hold{invoice.hold?.reason ? `: ${invoice.hold.reason}` : ''}
        </p>
      )}
      {(state === 'approved' || state === 'auto') && (
        <div className="space-y-3" data-testid="approve-result">
          <p className="flex items-center gap-2 text-primary-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Posted — this invoice's prices are recorded.</p>
          <PriceChangeTable rows={resultRows(invoice, items, before || {})} testid="approve-result-table" />
          <div className="flex flex-wrap gap-3">
            {costingLink && (
              <Link to="/pricing/impact" data-testid="approve-result-costing-link" className={`${big} bg-primary-500 text-surface hover:bg-primary-600 w-full sm:w-auto`}>
                See what this does to dish costs
              </Link>
            )}
            {canPrintLabels && <LotLabels labels={lotLabels} />}
            <Link to="/invoices" data-testid="approve-result-invoices-link" className={`${big} border border-surface-border text-ink hover:bg-surface-sunken w-full sm:w-auto`}>
              All invoices
            </Link>
            <Link to="/review" data-testid="approve-result-review-link" className={`${big} border border-surface-border text-ink hover:bg-surface-sunken w-full sm:w-auto`}>
              Review queue
            </Link>
            <Link to="/inventory" data-testid="approve-result-inventory-link" className={`${big} border border-surface-border text-ink hover:bg-surface-sunken w-full sm:w-auto`}>
              View stock
            </Link>
          </div>
        </div>
      )}
      {state === 'rejected' && (
        <p className="text-ink-muted" data-testid="rejected-banner">Rejected{invoice.rejection?.reason ? `: ${invoice.rejection.reason}` : ''}. No prices were written.</p>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        {canDecide && (state === 'rejected' || onHold) && (
          <button type="button" disabled={busy || posting} className={`${big} border border-primary-300 text-primary-700 hover:bg-primary-50`}
            data-testid="reread-btn"
            onClick={() => send('reread')}>
            <RefreshCw className="w-5 h-5" /> Read again
          </button>
        )}
        {mayApprove && (
          <button type="button" disabled={posting} className={`${big} bg-primary-500 text-surface hover:bg-primary-600`} data-testid="approve-btn"
            onClick={() => { setRevAction(revision?.choice === 'replaces' ? 'replace' : null); setDialog('approve'); }}>
            <CheckCircle2 className="w-5 h-5" /> Approve
          </button>
        )}
        {canDecide && state === 'needs_review' && (
          <button type="button" disabled className={`${big} bg-primary-500 text-surface`} data-testid="approve-btn-disabled">Approve</button>
        )}
        {canDecide && state === 'awaiting_approval' && !onHold && (
          <button type="button" disabled={posting} className={`${big} border border-warn/60 text-ink hover:bg-warn/10`} data-testid="hold-btn" onClick={() => setDialog('hold')}>
            <PauseCircle className="w-5 h-5" /> Hold
          </button>
        )}
        {canDecide && onHold && (
          <button type="button" disabled={busy} className={`${big} border border-warn/60 text-ink hover:bg-warn/10`} data-testid="release-hold-btn" onClick={() => send('release-hold')}>
            <PlayCircle className="w-5 h-5" /> Release hold
          </button>
        )}
        {canDecide && (state === 'awaiting_approval' || state === 'needs_review') && (
          <button type="button" disabled={posting} className={`${big} border border-danger/40 text-danger hover:bg-danger/5`} data-testid="reject-btn" onClick={() => setDialog('reject')}>
            <XCircle className="w-5 h-5" /> Reject
          </button>
        )}
        {canRemove && (
          <button type="button" className={`${big} border border-surface-border text-ink-muted hover:bg-surface-sunken`} data-testid="remove-upload-btn" onClick={() => setDialog('remove')}>
            <Trash2 className="w-5 h-5" /> Remove upload
          </button>
        )}
      </div>
      {error && !dialog && <p className="text-sm text-danger" data-testid="approve-error">{error}</p>}

      <ConfirmDialog open={dialog === 'approve'} title="Approve this invoice?" testid="approve-dialog" busy={busy}
        confirmLabel={revAction === 'replace' ? 'Replace and update prices' : 'Approve and update prices'} onCancel={close}
        confirmDisabled={(linked && (!revAction || (revAction === 'keep_both' && !reason.trim())))
          || (overs.length > 0 && !priceAck)}
        onConfirm={() => send('approve', {
          ...(linked ? { revision_action: revAction } : {}),
          ...(linked && revAction === 'keep_both' ? { override_duplicate_reason: reason.trim() } : {}),
          ...(overs.length ? { price_tolerance_ack: true } : {}),
        })}>
        {rows.some((r) => !r.history) && <p>These catalogue prices will change:</p>}
        {rows.some((r) => !r.history) && <PriceChangeTable rows={rows.filter((r) => !r.history)} testid="approve-preview-table" />}
        {overs.length > 0 && (
          <p className="text-sm text-warn" data-testid="price-tolerance-count">
            {overs.length} price change{overs.length === 1 ? '' : 's'} over {priceTolerancePct}%
          </p>
        )}
        {overs.length > 0 && (
          <label className="flex items-start gap-2 text-sm" data-testid="price-tolerance-ack">
            <input type="checkbox" checked={priceAck} onChange={(e) => setPriceAck(e.target.checked)} className="mt-1" />
            <span>I have seen the price moves beyond {priceTolerancePct}% (the supplier set these prices).</span>
          </label>
        )}
        {rows.some((r) => r.history) && (
          <p data-testid="approve-history-note" className="text-sm">
            {rows.every((r) => r.history) ? 'No catalogue price changes: ' : 'Also: '}
            {rows.filter((r) => r.history).length} item(s) have a newer price already held than this invoice
            ({invoice.date}), so they are recorded as history and today's price stays:{' '}
            {rows.filter((r) => r.history).map((r) => r.name).join(', ')}.
          </p>
        )}
        <p className="text-xs">Pack sizes (for example 10kg) are converted to a per-kg or per-g price when approved.</p>
        {linked && (
          <RevisionChoice revision={revision} action={revAction} onAction={setRevAction} reason={reason} onReason={setReason} />
        )}
        {error && <p className="text-danger" data-testid="approve-dialog-error">{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog open={dialog === 'reject' || dialog === 'hold'} testid={`${dialog}-dialog`} busy={busy}
        title={dialog === 'reject' ? 'Reject this invoice?' : 'Put this invoice on hold?'}
        confirmLabel={dialog === 'reject' ? 'Reject' : 'Hold'} danger={dialog === 'reject'} onCancel={close}
        confirmDisabled={!reason.trim()} onConfirm={() => send(dialog, dialog === 'reject'
          ? { reason: reason.trim(), ...(code ? { code } : {}),
            ...(code === 'misread' ? { problems: problemsPayload(invoice, problems) } : {}) }
          : { reason: reason.trim() })}>
        <label className="block">
          <span>{dialog === 'reject' ? 'Why? No prices will be written.' : 'Why? It stays waiting until you release it.'}</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid={`${dialog}-reason-input`}
            className="mt-1 w-full rounded-lg border border-surface-border p-2 text-ink" />
        </label>
        {dialog === 'reject' && (
          <RejectDetails invoice={invoice} code={code} onCode={setCode} rows={problems} onRows={setProblems} />
        )}
        {error && <p className="text-danger">{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog open={dialog === 'remove'} title="Remove this upload?" testid="remove-dialog" danger busy={busy}
        confirmLabel="Remove upload" onCancel={close} onConfirm={remove}>
        <p>This deletes the invoice, its review tasks and the stored file so the same file can be uploaded again. This cannot be undone.</p>
        {approved && <p className="text-danger" data-testid="remove-approved-warning">This invoice is approved: removing it also undoes the prices it set. An owner or admin must confirm it is them.</p>}
        {error && <p className="text-danger">{error}</p>}
      </ConfirmDialog>
      <StepUpDialog open={dialog === 'remove-step-up'} action="remove_approved_upload" testid="remove-step-up"
        summary={`Remove approved invoice ${invoice.invoice_no || ''} and undo the prices it set.`}
        perform={(token) => removeUpload(invoice.id, token)} onCancel={close}
        onDone={() => { close(); onRemoved(); }} />
    </section>
  );
}
